import { Fragment, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Dialog, Disclosure, Tab, Transition } from "@headlessui/react";

import { constants } from "../utils/ranges";

import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { ReactComponent as Caret } from "../assets/icons/Caret.svg";
import DigitalHealthAssessmentScore from "../assets/images/scores/DigitalHealthAssessmentScore.png";

const Range = ({
  ranges = [
    { point: 0, label: "Start", color: "#ccc" },
    { point: 100, label: "End", color: "#000" },
  ],
  selectedIndex,
  className = "",
}) => {
  selectedIndex -= ranges.length - 1 === selectedIndex;
  return (
    <div className={`relative grid grid-flow-col auto-cols-fr ${className}`}>
      {ranges.slice(0, -1).map((rangeItem, index, list) => (
        <div key={`range-${index}`} className="flex flex-col justify-end">
          {index === selectedIndex && (
            <Caret
              className="self-center shrink-0 w-2 h-1"
              style={{ color: ranges[selectedIndex].color }}
            />
          )}
          <h6
            className="opacity-60 text-center text-xxs font-medium uppercase truncate"
            style={
              index === selectedIndex
                ? { color: ranges[selectedIndex].color }
                : {}
            }
          >
            {rangeItem.label}
          </h6>
          <div
            className="my-1 h-1"
            style={{
              backgroundColor:
                index === selectedIndex ? ranges[selectedIndex].color : "#fff",
              borderTopLeftRadius: index === 0 ? 9999 : undefined,
              borderBottomLeftRadius: index === 0 ? 9999 : undefined,
              borderTopRightRadius:
                index === list.length - 1 ? 9999 : undefined,
              borderBottomRightRadius:
                index === list.length - 1 ? 9999 : undefined,
            }}
          />
          <p className="opacity-70 text-xxs">{rangeItem.point}</p>
        </div>
      ))}
      <p className="absolute bottom-0 right-0 opacity-70 text-xxs">
        {ranges[ranges.length - 1].point}
      </p>
    </div>
  );
};

const ListItemContainer = ({
  disabled = false,
  label = "",
  defaultExpanded = false,
  title,
  desc,
  className = "",
  ...props
}) => {
  return (
    <Disclosure
      defaultOpen={defaultExpanded}
      as="div"
      className="px-6 py-4 rounded-2xl border border-secondary bg-primary text-white shadow-box"
    >
      <Disclosure.Button as="div" disabled={disabled}>
        {({ open }) => [
          <div
            key="list-item-container-head"
            className="flex items-start justify-between space-x-1"
          >
            <h2 className="text-xs font-medium">{label}</h2>
            <Chevron
              className={`h-3 w-3 opacity-70 transition-transform duration-300 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>,
          <Fragment key="list-item-container-body">{props.children}</Fragment>,
        ]}
      </Disclosure.Button>
      <Disclosure.Panel>
        {typeof title === "string" && (
          <h5 className="mt-2.5 text-secondary text-xs font-medium">{title}</h5>
        )}
        {typeof desc === "string" && (
          <p className="mt-1.5 text-xxs font-light leading-tight whitespace-pre-line">
            {desc}
          </p>
        )}
      </Disclosure.Panel>
    </Disclosure>
  );
};

const ScanResult = () => {
  const { state } = useLocation();
  const result = state?.result;

  return result?.scan_id?.length > 0 ? (
    <section className="px-6 py-2 pb-8 space-y-6">
      <div>
        <h2 className="mb-6 text-xl font-semibold whitespace-pre-line">
          Digital Health
          <br />
          Assessment Score
        </h2>
        <div className="flex items-center">
          <div className="relative h-32 w-32">
            <div className="absolute inset-[12%] bg-gradient rounded-full -rotate-90 opacity-50" />
            <div className="absolute inset-0 bg-gradient rounded-full -rotate-90 opacity-25" />
            <div className="absolute inset-[24%] bg-gradient rounded-full -rotate-90 opacity-100" />
            <img
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10"
              src={DigitalHealthAssessmentScore}
              alt="Digital Health Assessment Score"
            />
          </div>
          <p className="text-secondary text-6xl font-bold ml-7">
            {Math.round(parseFloat(result?.wellness_score ?? 0) || 0) || "--"}
          </p>
        </div>
        <p className="mt-5 text-[11px] leading-snug text-justify opacity-70">
          Digital Health Risk Assessment Score is a numerical measurement of an
          individual's overall state of well-being, taking into account various
          aspects of physical, mental, and emotional health. A "good" wellbeing
          score typically refers to a score that indicates a high level of
          well-being, with low levels of stress, anxiety, and negative emotions,
          and high levels of physical and mental energy, resilience, and
          satisfaction with life.
        </p>
      </div>
      <h2 className="text-secondary text-sm font-semibold">Key Body Vitals</h2>
      <>
        {[
          { label: "Heart Rate", value: result?.vitals.heart_rate },
          { label: "Respiration Rate", value: result?.vitals.resp_rate },
          {
            label: "Oxygen Saturation",
            value: result?.vitals.oxy_sat_prcnt,
          },
        ].map((item, index) => {
          const ranges =
            item.label === "Heart Rate"
              ? constants[item.label].ranges[result?.posture ?? "resting"]
              : constants[item.label].ranges;
          const selectedRangeIndex = ranges.reduce(
            (p, c, ci) => (item.value >= c.point ? ci : p),
            0
          );
          return (
            <ListItemContainer
              key={`section-item-${index}`}
              label={item.label}
              title={`Your ${item.label} is ${ranges[selectedRangeIndex].label}`}
              desc={
                constants[item.label].info +
                "\n\n" +
                ranges[selectedRangeIndex].desc
              }
            >
              <div className="py-3 flex items-center justify-between space-x-2">
                <div className="shrink-0 basis-1/4 text-center font-semibold">
                  <h4 className="text-secondary text-xl">{item.value}</h4>
                  <h5 className="opacity-70 text-xxs">
                    {constants[item.label].unit}
                  </h5>
                </div>
                <Range
                  className="grow"
                  ranges={ranges}
                  selectedIndex={selectedRangeIndex}
                />
              </div>
            </ListItemContainer>
          );
        })}
        {(() => {
          const sys = result?.vitals.bp_sys;
          const dia = result?.vitals.bp_dia;
          const selectedSYSRangeIndex =
            sys !== "--"
              ? constants["Blood Pressure"].ranges[0].reduce(
                  (p, c, ci) => (sys > c.point ? ci : p),
                  0
                )
              : undefined;
          const selectedDIARangeIndex =
            dia !== "--"
              ? constants["Blood Pressure"].ranges[1].reduce(
                  (p, c, ci) => (dia > c.point ? ci : p),
                  0
                )
              : undefined;
          return (
            <ListItemContainer
              key="section-item-3"
              label="Blood Pressure"
              title={`Your Blood Pressure is ${constants["Blood Pressure"].ranges[0][selectedSYSRangeIndex]?.label}`}
              desc={
                constants["Blood Pressure"].info +
                "\n\n" +
                constants["Blood Pressure"].ranges[0][selectedSYSRangeIndex]
                  ?.desc
              }
              disabled={
                selectedSYSRangeIndex === undefined ||
                selectedDIARangeIndex === undefined
              }
            >
              <div className="py-3 flex items-center justify-between space-x-2">
                <div className="shrink-0 basis-1/4 text-center">
                  <div className="font-semibold">
                    <h4 className="inline text-secondary text-xl">{sys}</h4>
                    &nbsp;
                    <h5 className="inline opacity-70 text-xxs">
                      {constants["Blood Pressure"].unit}
                    </h5>
                  </div>
                  <p className="text-ternary text-xxs font-medium">Systolic</p>
                </div>
                <Range
                  className="grow"
                  ranges={constants["Blood Pressure"].ranges[0]}
                  selectedIndex={selectedSYSRangeIndex}
                />
              </div>
              <div className="py-3 flex items-center justify-between space-x-2">
                <div className="shrink-0 basis-1/4 text-center">
                  <div className="font-semibold">
                    <h4 className="inline text-secondary text-xl">{dia}</h4>
                    &nbsp;
                    <h5 className="inline opacity-70 text-xxs">
                      {constants["Blood Pressure"].unit}
                    </h5>
                  </div>
                  <p className="text-ternary text-xxs font-medium">Diastolic</p>
                </div>
                <Range
                  className="grow"
                  ranges={constants["Blood Pressure"].ranges[1]}
                  selectedIndex={selectedDIARangeIndex}
                />
              </div>
            </ListItemContainer>
          );
        })()}
      </>
      <div className="p-6 rounded-2xl flex flex-col items-center text-center bg-gradient-transparent text-white">
        <h4 className="text-xs font-medium">Disclaimer</h4>
        <p className="mt-2 text-xxs text-justify">
          For Investigational Use Only. CarePlix is not a substitute for the
          clinical judgment of a healthcare professional. CarePlix is intended
          to improve your awareness of general wellness. CarePlix does not
          diagnose, treat, mitigate or prevent any disease, symptom, disorder or
          abnormal physical state. Consult with a healthcare professional or
          emergency services if you believe you may have a medical issue.
        </p>
        <Link
          className="mt-4 px-4 py-2 rounded-full bg-white text-primary text-xs font-medium"
          to="/"
          replace
        >
          Dashboard
        </Link>
      </div>
    </section>
  ) : (
    <Navigate to="/" replace />
  );
};

export default ScanResult;
